import { useState } from 'react';
import Card from '../ui/Card';
import CardTitle from '../ui/CardTitle';
import Flex from '../ui/Flex';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import ProxiesText from './ProxiesText';
import ProxiesTable from './ProxiesTable';
import { LucideCopy, LucideDownload } from 'lucide-react';
import { downloadAsPlainText } from '@/utils/downloadAsPlainText';
import formatProxies from '@/utils/formatProxies';
import useProxiesInPool from '@/hooks/queries/useProxiesInPool';
import usePoolById from '@/hooks/queries/usePoolById';
import { copyToClipboard } from '@/utils/copyToClipboard';
import clsx from 'clsx';
import { useToast } from '@/hooks/useToast';
import ProxiesList from './ProxiesList';
import { DesktopView, MobileView } from '../responsive';
import GenerateProxies from '@/components/ProxyPoolDetails/GenerateProxies';

const formatOptions = [
  'Hostname:Port:Username:Password',
  'Hostname:Port@Username:Password',
  'Username:Password:Hostname:Port',
  'Protocol://Username:Password@Hostname:Port',
];

export default function Proxies({ poolId }: { poolId: number }) {
  const [view, setView] = useState<'table' | 'list'>('table');
  const [format, setFormat] = useState('Hostname:Port:Username:Password');
  const { toast } = useToast();

  const { data: proxies } = useProxiesInPool(poolId);
  const { data: pool } = usePoolById(poolId);

  function handleDownload() {
    if (!proxies || !pool) return;
    toast({
      description: (
        <Flex.Row className="flex items-center gap-2">
          <LucideDownload className="inline-block h-4 -mt-[1.5px] stroke-indigo-600" />
          Downloading
        </Flex.Row>
      ),
    });
    downloadAsPlainText(formatProxies(proxies, pool, format));
  }

  function handleCopy() {
    if (!proxies || !pool) return;
    copyToClipboard(formatProxies(proxies, pool, format));
    toast({
      description: (
        <Flex.Row className="flex items-center gap-2">
          <LucideCopy className="inline-block h-4 -mt-[1.5px] stroke-indigo-600" />
          Copied to clipboard
        </Flex.Row>
      ),
    });
  }

  const copyAndDownloadButtons = (
    <Flex.Row className="w-full sm:w-fit gap-6 items-center pr-2 justify-between sm:justify-start">
      <Flex.Row
        className="gap-2 items-center cursor-pointer hover:opacity-70"
        onClick={handleCopy}
      >
        <LucideCopy className="inline-block h-4" />
        <span className="text-sm whitespace-nowrap">Copy proxy list</span>
      </Flex.Row>
      <Flex.Row
        className="gap-2 items-center cursor-pointer hover:opacity-70"
        onClick={handleDownload}
      >
        <LucideDownload className="inline-block h-4" />
        <span className="text-sm whitespace-nowrap">Download</span>
      </Flex.Row>
    </Flex.Row>
  );

  return (
    <Card className="flex flex-col gap-4">
      <CardTitle>
        <a href="#proxies">Proxies</a>
      </CardTitle>
      <Tabs
        value={view}
        onValueChange={(value) => setView(value as 'table' | 'list')}
      >
        <Flex.Row className="items-center justify-between">
          <div className="gap-3 items-start sm:items-center flex flex-col sm:flex-row w-full pb-2 sm:pb-0">
            <TabsList className="grid w-full sm:w-fit grid-cols-2">
              <TabsTrigger value="table">As table</TabsTrigger>
              <TabsTrigger value="list">As list</TabsTrigger>
            </TabsList>
            <div className={clsx('w-full', view === 'table' && 'hidden')}>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="w-full sm:w-[360px] h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {formatOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <MobileView>{copyAndDownloadButtons}</MobileView>
          </div>
          <DesktopView>{copyAndDownloadButtons}</DesktopView>
        </Flex.Row>
        <TabsContent value="table">
          <DesktopView>
            <ProxiesTable poolId={poolId} />
          </DesktopView>
          <MobileView>
            <ProxiesList poolId={poolId} />
          </MobileView>
        </TabsContent>
        <TabsContent value="list">
          <ProxiesText poolId={poolId} format={format} />
        </TabsContent>
      </Tabs>
      <MobileView>
        <Flex.Col className="gap-2">
          <div className="text-sm font-medium">Add proxies</div>
          <GenerateProxies poolId={poolId} />
        </Flex.Col>
      </MobileView>
    </Card>
  );
}
